import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

export const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export const DEFAULT_TIME_FORMAT = '12h';

export const normalizeTimeFormat = format => {
  if (!format) return DEFAULT_TIME_FORMAT;

  const normalized = format.toString().trim().toLowerCase();

  if (['24h', '24-hour', '24hours', '24'].includes(normalized)) {
    return '24h';
  }

  return DEFAULT_TIME_FORMAT;
};

const buildEmptySlots = () =>
  WEEKDAYS.reduce((acc, day) => {
    acc[day] = [];
    return acc;
  }, {});

const normalizeSlot = (slot, day) => {
  if (!slot) return null;

  const duration =
    typeof slot.duration_minutes === 'number'
      ? slot.duration_minutes
      : slot.duration_minutes
        ? parseInt(slot.duration_minutes, 10)
        : undefined;

  return {
    ...slot,
    duration_minutes: Number.isNaN(duration) ? undefined : duration,
    weekday: day,
  };
};

export const normalizeAvailableSlots = slots => {
  if (!slots) return buildEmptySlots();

  if (Array.isArray(slots)) {
    return WEEKDAYS.reduce((acc, day) => {
      acc[day] = slots
        .filter(slot => {
          if (!slot) return false;
          const weekdayRaw = slot.weekday;
          if (!weekdayRaw) return true;
          return weekdayRaw.toString().toLowerCase() === day;
        })
        .map(slot => normalizeSlot(slot, day))
        .filter(Boolean);
      return acc;
    }, buildEmptySlots());
  }

  return WEEKDAYS.reduce((acc, day) => {
    const daySlots = slots[day] || slots[day.toUpperCase()] || slots[day.charAt(0).toUpperCase() + day.slice(1)] || [];

    acc[day] = Array.isArray(daySlots)
      ? daySlots
          .map(slot =>
            normalizeSlot(
              {
                ...slot,
                weekday: slot?.weekday?.toString().toLowerCase() || day,
              },
              day,
            ),
          )
          .filter(Boolean)
      : [];
    return acc;
  }, buildEmptySlots());
};

export const getSlotIdentifier = slot => slot?.alias || slot?.name || slot?.time || null;

export const getDefaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';
  } catch (_err) {
    return 'Etc/UTC';
  }
};

export const formatCurrentTime = (tz, displayFormat) => {
  try {
    const hour12 = displayFormat !== '24h';
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12,
      hourCycle: hour12 ? 'h12' : 'h23',
      hour: '2-digit',
      minute: '2-digit',
    });
    return formatter.format(new Date());
  } catch (_err) {
    return '';
  }
};

export const todayKeyInTimezone = tz => {
  try {
    return dayjs()
      .tz(tz || 'Etc/UTC')
      .format('YYYY-MM-DD');
  } catch (_err) {
    return dayjs().format('YYYY-MM-DD');
  }
};

export const buildTimezoneOptions = tzList =>
  (tzList || []).map(tz => {
    const segments = tz.split('/');
    const city = segments[segments.length - 1]?.replace(/_/g, ' ') || tz;
    return { value: tz, label: city };
  });

export const guessTimeFormat = (tz, fallbackFormat = DEFAULT_TIME_FORMAT) => {
  try {
    const preferredLocales = ['en-GB', 'fr-FR', 'de-DE'];
    for (const locale of preferredLocales) {
      const cycle = new Intl.DateTimeFormat(locale, { hour: 'numeric', timeZone: tz }).resolvedOptions().hourCycle;
      if (cycle && cycle.includes('23')) return '24h';
    }
    const fallbackCycle = new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZone: tz }).resolvedOptions()
      .hourCycle;
    return fallbackCycle && fallbackCycle.includes('12') ? '12h' : '24h';
  } catch (_err) {
    return fallbackFormat;
  }
};

export const slotStartsInPast = (dateString, timeString, sourceTimezone, displayTimezone) => {
  if (!dateString || !timeString) return false;

  try {
    const baseTz = sourceTimezone || 'Etc/UTC';
    const start = dayjs.tz(`${dateString}T${timeString}`, baseTz);
    const displayStart = displayTimezone ? start.tz(displayTimezone) : start.local();
    const now = displayTimezone ? dayjs().tz(displayTimezone) : dayjs();

    return displayStart.valueOf() <= now.valueOf();
  } catch (_err) {
    return false;
  }
};

const getEndTime = (start, minutes) => {
  const [h, m] = (start || '00:00').split(':').map(Number);
  const dt = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  dt.setMinutes(dt.getMinutes() + (minutes || 0));
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
};

export const toLocalTimeRange = (dateString, timeString, durationMinutes, format, sourceTimezone, displayTimezone) => {
  if (!timeString || !dateString) {
    const fallbackEnd =
      typeof durationMinutes === 'string'
        ? durationMinutes
        : timeString && typeof durationMinutes === 'number'
          ? getEndTime(timeString, durationMinutes)
          : null;

    return {
      startText: timeString || '',
      endText: fallbackEnd || '',
      startLocal: null,
      endLocal: null,
    };
  }

  try {
    const baseTz = sourceTimezone || 'Etc/UTC';
    const start = dayjs.tz(`${dateString}T${timeString}`, baseTz);
    const end =
      typeof durationMinutes === 'number'
        ? start.add(durationMinutes, 'minute')
        : dayjs.tz(`${dateString}T${durationMinutes}`, baseTz);

    const startLocal = displayTimezone ? start.tz(displayTimezone) : start.local();
    const endLocal = displayTimezone ? end.tz(displayTimezone) : end.local();
    const displayFormat = normalizeTimeFormat(format) === '24h' ? 'HH:mm' : 'h:mm A';

    return {
      startText: startLocal.format(displayFormat),
      endText: endLocal.format(displayFormat),
      startLocal,
      endLocal,
    };
  } catch (_error) {
    const fallbackEnd =
      typeof durationMinutes === 'string'
        ? durationMinutes
        : getEndTime(timeString, typeof durationMinutes === 'number' ? durationMinutes : 0);

    return {
      startText: timeString,
      endText: fallbackEnd,
      startLocal: null,
      endLocal: null,
    };
  }
};
